import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ENV from '../config/env';

const CompanySignup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const billingParam = searchParams.get('billing') || 'monthly';
  
  const [formData, setFormData] = useState({
    companyName: '',
    adminEmail: '',
    seatQuantity: 3,
    billingInterval: billingParam as 'monthly' | 'annual'
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  
  const basePriceMonthly = 49;
  const basePriceAnnual = 490;
  const additionalUserMonthly = 15;
  const additionalUserAnnual = 150;
  
  const calculatePrice = () => {
    const additionalSeats = Math.max(0, formData.seatQuantity - 3);
    if (formData.billingInterval === 'monthly') {
      return basePriceMonthly + (additionalSeats * additionalUserMonthly);
    } else {
      return basePriceAnnual + (additionalSeats * additionalUserAnnual);
    }
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }
    
    if (!formData.adminEmail.trim()) {
      newErrors.adminEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      newErrors.adminEmail = 'Please enter a valid email address';
    }
    
    if (formData.seatQuantity < 1) {
      newErrors.seatQuantity = 'At least 1 seat is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const apiUrl = `${ENV.API_URL}/api/billing-company/checkout`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          companyName: formData.companyName,
          planId: 'company',
          seatQuantity: formData.seatQuantity,
          adminEmail: formData.adminEmail,
          billingInterval: formData.billingInterval
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }
      
      const { url } = await response.json();
      window.location.href = url;
      
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      setErrors({ submit: error.message || 'Failed to start checkout. Please try again.' });
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-2">
            Start Your Team Trial
          </h1>
          <p className="text-xl text-gray-600">7-day free trial • No charge until day 8</p>
        </div>
        
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                {formData.billingInterval === 'monthly' ? 'Monthly Plan' : 'Annual Plan'}
              </h3>
              <p className="text-sm text-gray-600">{formData.seatQuantity} users</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-indigo-600">£{calculatePrice()}</div>
              <div className="text-sm text-gray-600">/{formData.billingInterval === 'monthly' ? 'month' : 'year'}</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xl border-2 border-indigo-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
              <input
                type="text"
                id="companyName"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className={`w-full px-4 py-3 border ${errors.companyName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                placeholder="Acme Corporation"
                disabled={isLoading}
              />
              {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>}
            </div>
            
            <div>
              <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-700 mb-2">Your Email (Admin Account) *</label>
              <input
                type="email"
                id="adminEmail"
                value={formData.adminEmail}
                onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                className={`w-full px-4 py-3 border ${errors.adminEmail ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                placeholder="admin@company.com"
                disabled={isLoading}
              />
              {errors.adminEmail && <p className="mt-1 text-sm text-red-600">{errors.adminEmail}</p>}
            </div>
            
            <div>
              <label htmlFor="seatQuantity" className="block text-sm font-medium text-gray-700 mb-2">Number of Team Members *</label>
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, seatQuantity: Math.max(1, formData.seatQuantity - 1) })}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold transition-colors"
                  disabled={isLoading || formData.seatQuantity <= 1}
                >
                  -
                </button>
                <input
                  type="number"
                  id="seatQuantity"
                  value={formData.seatQuantity}
                  onChange={(e) => setFormData({ ...formData, seatQuantity: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="w-24 px-4 py-3 border border-gray-300 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  min="1"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, seatQuantity: formData.seatQuantity + 1 })}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold transition-colors"
                  disabled={isLoading}
                >
                  +
                </button>
              </div>
              {errors.seatQuantity && <p className="mt-1 text-sm text-red-600">{errors.seatQuantity}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Billing Interval</label>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, billingInterval: 'monthly' })}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                    formData.billingInterval === 'monthly'
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  disabled={isLoading}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, billingInterval: 'annual' })}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                    formData.billingInterval === 'annual'
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  disabled={isLoading}
                >
                  Annual (Save 17%)
                </button>
              </div>
            </div>
            
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {errors.submit}
              </div>
            )}
            
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transform transition-all hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? 'Processing...' : 'Continue to Payment'}
            </button>
            
            <p className="text-center text-sm text-gray-500">
              Secure payment via Stripe • Cancel anytime during trial
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompanySignup;
